/* ENSSA Reporting — Portal Home (Level 0). System-level cross-district view. */

import React from "react";
import {
  Card, Eyebrow, KPI, Section, Btn, CSVExportButton, ScreeningDisclaimer,
} from "@/components/branded/Components";
import { RiskBreakdownStackedBar, TaskBoxPlotPanel } from "@/components/charts/Charts";
import { RiskTransitionSankey } from "@/components/charts/sankey";
import { domainRiskLabel, riskLabel } from "@/lib/copy";
import { allStudents, riskCountsOf, taskBoxStats, getMultiStageTransitions } from "@/lib/report-data";
import type { AggregateNode, District } from "@/types";
import { DemographicFilterBar } from "@/components/branded/DemographicFilterBar";
import { NO_DEMOGRAPHIC_FILTERS, makeDemographicPredicate, isDemographicFilterActive } from "@/lib/demographic-filters";
import type { DemographicFilters } from "@/lib/demographic-filters";

interface PortalHomeProps {
  districts: District[];
  onOpenDistrict: (id: string) => void;
}

function PortalHome({ districts, onOpenDistrict }: PortalHomeProps) {
  const [demoFilters, setDemoFilters] = React.useState<DemographicFilters>(NO_DEMOGRAPHIC_FILTERS);
  const demoPredicate = React.useMemo(() => makeDemographicPredicate(demoFilters), [demoFilters]);
  const allDistrictsNode: AggregateNode = { schools: districts.flatMap(d => d.schools) };
  const totalSchools = districts.reduce((a,d) => a + d.schools.length, 0);
  const totalStudents = allStudents(allDistrictsNode).length;
  const breakF = riskCountsOf(allDistrictsNode, 'F', demoPredicate);
  const breakY1 = riskCountsOf(allDistrictsNode, 'Y1', demoPredicate);
  const all = riskCountsOf(allDistrictsNode, undefined, demoPredicate);
  const transitions = getMultiStageTransitions(allDistrictsNode, undefined, demoPredicate);
  const stats = taskBoxStats(allDistrictsNode, undefined, undefined, demoPredicate);

  return (
    <>
      <div style={{ marginBottom: 24 }}><ScreeningDisclaimer /></div>

      <DemographicFilterBar
        value={demoFilters}
        onChange={setDemoFilters}
        totalCount={totalStudents}
        filteredCount={all.total}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 18, marginBottom: 32 }}>
        <KPI label="Districts reporting" value={districts.length} />
        <KPI label="Schools" value={totalSchools} sub="reporting this term" />
        <KPI label="Students screened" value={isDemographicFilterActive(demoFilters) ? all.total : totalStudents} sub="Foundation + Year 1" />
        <KPI label={`${riskLabel('high')} performance band`} value={all.high} accent="var(--risk-concern-fg)" sub={all.total ? `${Math.round(all.high/all.total*100)}% of cohort` : '—'} />
      </div>

      <Section eyebrow="Population breakdown" title="Performance Bands by Year Level"
        hint="Stacked bars show the proportion of students in each band, by year level, for the selected term.">
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18, alignItems: 'center', marginBottom: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Foundation</div>
            <RiskBreakdownStackedBar counts={breakF} showLabels />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18, alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Year 1</div>
            <RiskBreakdownStackedBar counts={breakY1} showLabels />
          </div>
        </Card>
      </Section>

      <Section eyebrow="Movement" title="Band Changes from Term to Term"
        hint="Ribbons show how students moved between bands across screening windows. Hover a ribbon to focus it.">
        <Card><RiskTransitionSankey transitions={transitions} /></Card>
      </Section>

      <Section eyebrow="Task profile" title="Where the Cohort Is Strong and Weak"
        hint="Each row is the percentile distribution across all students for one task. Lower medians indicate a system-wide gap.">
        <Card><TaskBoxPlotPanel stats={stats} /></Card>
      </Section>

      <Section eyebrow="Districts" title="Drill into a Region"
        controls={<CSVExportButton scope="districts" />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 18 }}>
          {districts.map(d => {
            const dn: AggregateNode = { schools: d.schools };
            const counts = riskCountsOf(dn);
            return (
              <Card key={d.id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <Eyebrow>District</Eyebrow>
                    <h3 style={{ fontSize: 18, fontWeight: 700, margin: '4px 0 0' }}>{d.name}</h3>
                    <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 4 }}>
                      {d.schools.length} schools · {counts.total} students
                    </div>
                  </div>
                  <Btn variant="ghost" onClick={() => onOpenDistrict(d.id)}>Open →</Btn>
                </div>
                <RiskBreakdownStackedBar counts={counts} showLegend={false} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--fg-3)', flexWrap: 'wrap', gap: 8 }}>
                  <span><strong style={{ color: 'var(--risk-on-track-fg)', fontFamily: 'var(--font-mono)' }}>{counts.low}</strong> {domainRiskLabel('low').toLowerCase()}</span>
                  <span><strong style={{ color: 'var(--risk-emerging-fg)', fontFamily: 'var(--font-mono)' }}>{counts.moderate}</strong> {domainRiskLabel('moderate').toLowerCase()}</span>
                  <span><strong style={{ color: 'var(--risk-concern-fg)', fontFamily: 'var(--font-mono)' }}>{counts.high}</strong> {domainRiskLabel('high').toLowerCase()}</span>
                  <span><strong style={{ fontFamily: 'var(--font-mono)' }}>{counts.more_info}</strong> {domainRiskLabel('more_info').toLowerCase()}</span>
                </div>
              </Card>
            );
          })}
        </div>
      </Section>
    </>
  );
}

export { PortalHome };
export default PortalHome;
